import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/site/Footer";
import Nav from "../components/site/Nav";
import Reveal from "../components/site/Reveal";
import ShowcaseVideo from "../components/site/ShowcaseVideo";
import {
  ArrowUpRight,
  ChevronLeft,
  Image as ImageIcon,
  Video as VideoIcon,
  Link2,
} from "lucide-react";
import { showcaseItems, showcaseFilters } from "../data/showcase";

const TypeIcon = ({ type, size = 14 }) => {
  if (type === "video") return <VideoIcon size={size} />;
  if (type === "link") return <Link2 size={size} />;
  return <ImageIcon size={size} />;
};

const Tile = ({ item }) => {
  if (item.type === "video") {
    return (
      <div
        data-testid={`showcase-tile-${item.id}`}
        className="group block bg-[var(--lt-cream)]/60 border border-[var(--lt-line)] hover:border-[var(--lt-charcoal)]/40 transition-colors duration-300"
      >
        <div className="lt-img-wrap aspect-[4/5] relative overflow-hidden bg-black">
          <ShowcaseVideo item={item} />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[var(--lt-peace)]/90 text-[var(--lt-charcoal)] px-2 py-1 text-[10px] uppercase tracking-[0.18em] z-10">
            <TypeIcon type={item.type} /> {item.type}
          </span>
        </div>
        <TileMeta item={item} />
      </div>
    );
  }

  const isLink = item.type === "link";
  const Wrapper = isLink ? "a" : "div";
  const wrapperProps = isLink ? { href: item.url, target: "_blank", rel: "noreferrer" } : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`showcase-tile-${item.id}`}
      className="group block bg-[var(--lt-cream)]/60 border border-[var(--lt-line)] hover:border-[var(--lt-charcoal)]/40 transition-colors duration-300"
    >
      <div className="lt-img-wrap aspect-[4/5] relative overflow-hidden bg-[var(--lt-cream)]">
        <img
          src={item.src || item.thumbnail}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[var(--lt-peace)]/90 text-[var(--lt-charcoal)] px-2 py-1 text-[10px] uppercase tracking-[0.18em]">
          <TypeIcon type={item.type} /> {item.type}
        </span>
        {isLink && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[var(--lt-charcoal)] text-[var(--lt-peace)] px-2 py-1 text-[10px] uppercase tracking-[0.18em]">
            Open <ArrowUpRight size={11} />
          </span>
        )}
      </div>
      <TileMeta item={item} />
    </Wrapper>
  );
};

const TileMeta = ({ item }) => (
  <div className="p-5">
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <p className="eyebrow">{item.client}</p>
      {item.tags?.length > 0 && (
        <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--lt-bark)]/60">
          {item.tags.join(" · ")}
        </span>
      )}
    </div>
    <h3
      className="font-display tracking-editorial text-[var(--lt-charcoal)] leading-tight mb-2"
      style={{ fontSize: "1.4rem", fontWeight: 600 }}
    >
      {item.title}
    </h3>
    {item.description && (
      <p className="text-sm text-[var(--lt-bark)] leading-relaxed line-clamp-3">
        {item.description}
      </p>
    )}
  </div>
);

const Showcase = () => {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return showcaseItems;
    return showcaseItems.filter((i) => i.type === filter.toLowerCase());
  }, [filter]);

  return (
    <main data-testid="showcase-page" className="bg-[var(--lt-peace)] font-body">
      <Nav />

      <section className="pt-36 lg:pt-44 pb-16 lg:pb-20">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal>
            <Link
              to="/"
              data-testid="showcase-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--lt-bark)] hover:text-[var(--lt-charcoal)] mb-10"
            >
              <ChevronLeft size={14} /> Back to home
            </Link>
            <p className="eyebrow mb-5">/ Showcase</p>
            <h1
              data-testid="showcase-headline"
              className="font-display tracking-editorial text-[var(--lt-charcoal)] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 6vw, 5.4rem)", fontWeight: 600 }}
            >
              The <em className="italic text-[var(--lt-bark)]">work</em>.
            </h1>
            <p className="mt-8 max-w-2xl text-[var(--lt-bark)] text-base lg:text-lg leading-relaxed">
              Selected content, campaigns and links from current and past studio engagements.
              Updated regularly.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div
              className="mt-12 flex flex-wrap items-center gap-2"
              data-testid="showcase-filters"
            >
              {showcaseFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`showcase-filter-${f.toLowerCase()}`}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                    filter === f
                      ? "bg-[var(--lt-charcoal)] text-[var(--lt-peace)] border-[var(--lt-charcoal)]"
                      : "text-[var(--lt-charcoal)] border-[var(--lt-line)] hover:border-[var(--lt-charcoal)]"
                  }`}
                >
                  {f}
                </button>
              ))}
              <span className="ml-auto text-xs text-[var(--lt-bark)]/60">
                {filtered.length} {filtered.length === 1 ? "item" : "items"}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-32 lg:pb-40">
        <div className="container mx-auto px-6 lg:px-10">
          {filtered.length === 0 ? (
            <div
              className="py-20 text-center text-[var(--lt-bark)]/70 italic"
              data-testid="showcase-empty"
            >
              Nothing here yet under <strong>{filter}</strong>. New work coming soon.
            </div>
          ) : (
            <div
              data-testid="showcase-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            >
              {filtered.map((item, i) => (
                <Reveal key={item.id} delay={i * 60}>
                  <Tile item={item} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Showcase;
